/**
 * What an estimate line says the work is.
 *
 * A line built from the catalogue used to be labelled "<category> | <name>",
 * assembled in four places that had each written the same two lines. That is
 * the warehouse's name for a thing — "Shingles | GAF Timberline HDZ ARM" — and
 * it is what the customer reads on the estimate, the proposal and the contract,
 * because `estimate_items.description` is the customer-facing label.
 *
 * From 2026-08-31 a product may carry its own wording (Jonathon: "when we add a
 * product we can add estimate line item verbiage that shows to the customer").
 * When it does, that is what the line says. When it does not, nothing changes.
 *
 * Deliberately not `products.description`: that column is the blurb under a
 * proposal upgrade and holds imported text on hundreds of products, none of it
 * written to be read by a customer.
 */

export interface ProductWording {
  name?: string | null;
  category?: string | null;
  customer_wording?: string | null;
}

/** The catalogue's own label — what a line said before wording existed. */
export function catalogueLabel(product: ProductWording): string {
  return [product.category, product.name].filter(Boolean).join(" | ");
}

/**
 * The description to write on an estimate line for this product.
 *
 * Whitespace-only wording is treated as unset: a box somebody opened and left
 * alone should not blank the line label.
 */
export function estimateLineDescription(product: ProductWording): string {
  const wording = (product.customer_wording ?? "").trim();
  return wording === "" ? catalogueLabel(product) : wording;
}
